import _ from 'lodash';
// Bypasses the shared/ui barrel: routing through it would create an import cycle, since the
// barrel also re-exports FabricJS/AIECGViewer, which imports @/entities/ecg.
import { ANNOTATION_TYPE } from '@/shared/ui/components/FabricJS/Constants';
import { TYPE_BEAT_ENUM } from '@/shared/config';
import { CONFIG_ECG } from '@/entities/ecg/@x/beat';
import { isSameBeats } from '@/shared/lib';

const beatTypeStringConvert = {
  60: 'V',
  70: 'S',
  80: 'Q',
  90: 'N',
  100: 'Q',
  1000: 'DEL', // Special case for delete beat in ecg viewer
};

/**
 * Maps a numeric HES beat status onto its single-letter beat type. Looks the status up in beatTypeStringConvert and falls back to N.
 *
 * @param hesBeatStatus - Numeric HES beat status to translate.
 * @returns The beat type letter, defaulting to N.
 */
export const generateBeatTypeString = (hesBeatStatus: any) =>
  (beatTypeStringConvert as any)[hesBeatStatus] || 'N';

const hesBeatStatusConvert = {
  V: 60,
  S: 70,
  Q: 80,
  N: 90,
  DEL: 1000, // Special case for delete beat in ecg viewer
};

/**
 * Maps a single-letter beat type back onto its numeric HES status. Looks the letter up in hesBeatStatusConvert and falls back to one.
 *
 * @param beatTypeString - Beat type letter such as N, S, V or Q.
 * @returns The numeric HES beat status, defaulting to one.
 */
export const generateHesBeatStatus = (beatTypeString: any) =>
  (hesBeatStatusConvert as any)[beatTypeString] || 1;

export const updateStripReview = ({
  stripChanges = [] as any[],
  strip,
  isReviewed,
}: {
  stripChanges?: any[];
  strip: any;
  isReviewed: any;
}) => {
  const cloneStripChanges = _.cloneDeep(stripChanges);
  let indexRemove;
  let hasBeenChanged;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    if (cloneStripChanges[i].id === strip?.id) {
      hasBeenChanged = true;
      if (strip.isReviewed === isReviewed) {
        if (_.isNil(strip.type)) {
          indexRemove = i;
          break;
        }
      }
      const stripChangeEl = cloneStripChanges[i];
      if (stripChangeEl.oldReviewed === isReviewed) {
        if (_.isNil(stripChangeEl.type)) {
          indexRemove = i;
          break;
        }
      }
      if (stripChangeEl.isReviewed === isReviewed) {
        return cloneStripChanges;
      }
      stripChangeEl.isReviewed = isReviewed;
      stripChangeEl.oldReviewed = stripChangeEl.oldReviewed || stripChangeEl.isReviewed;
    }
  }
  if (!_.isNil(indexRemove)) {
    cloneStripChanges.splice(indexRemove, 1);
  }
  if (!hasBeenChanged && strip.isReviewed !== isReviewed) {
    cloneStripChanges.push({
      ...strip,
      ...{
        isReviewed,
        oldReviewed: strip.isReviewed,
        oldType: strip.type,
      },
    });
  }
  return cloneStripChanges;
};

export const updateMultipleStripReview = ({
  stripChanges = [] as any[],
  strips = [] as any[],
  isReviewed,
}: {
  stripChanges?: any[];
  strips?: any[];
  isReviewed: any;
}) => {
  const cloneStripChanges = _.cloneDeep(stripChanges);
  const indexRemoves: any[] = [];
  const idHasBeenChangedStrips: any[] = [];
  let breakFirstLoop = false;
  let findCounter = 0;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    for (let j = 0; j < strips.length; j += 1) {
      if (cloneStripChanges[i].id === strips[j].id) {
        findCounter += 1;
        //* If done search for all strips then early break the loop
        if (findCounter === strips.length) {
          breakFirstLoop = true;
        }
        idHasBeenChangedStrips.push(strips[j].id);
        if (strips[j].isReviewed === isReviewed) {
          if (_.isNil(strips[j].type)) {
            indexRemoves.push(i);
            break;
          }
        }
        const stripChangeEl = cloneStripChanges[i];
        if (stripChangeEl.oldReviewed === isReviewed) {
          if (_.isNil(stripChangeEl.type)) {
            indexRemoves.push(i);
            break;
          }
        }
        if (stripChangeEl.isReviewed === isReviewed) {
          break;
        }
        stripChangeEl.isReviewed = isReviewed;
        stripChangeEl.oldReviewed = stripChangeEl.oldReviewed || stripChangeEl.isReviewed;
      }
    }
    if (breakFirstLoop) {
      break;
    }
  }
  // Splice in reverse order to prevent mess up index
  for (let i = indexRemoves.length - 1; i >= 0; i -= 1) {
    cloneStripChanges.splice(indexRemoves[i], 1);
  }
  _.forEach(strips, (strip) => {
    if (!idHasBeenChangedStrips.includes(strip.id) && strip.isReviewed !== isReviewed) {
      cloneStripChanges.push({
        ...strip,
        ...{
          isReviewed,
          oldReviewed: strip.isReviewed,
          oldType: strip.type,
        },
      });
    }
  });
  return cloneStripChanges;
};

export const updateStripType = ({
  stripChanges = [] as any[],
  strip,
  type,
  isEvent = true,
  isReset,
}: {
  stripChanges?: any[];
  strip: any;
  type: any;
  isEvent?: boolean;
  isReset?: any;
}) => {
  // case beat current type === update type => remove if in beat change
  // case beat has changed and update to new type === original type => remove if in beat change
  // case beat has changed and update to new type === current type => unchange
  // case beat has changed and update to new type !== original type
  const cloneStripChanges = _.cloneDeep(stripChanges);
  let indexRemove;
  let hasBeenChanged;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    const compareField = isEvent ? 'idEvent' : 'id';
    if (cloneStripChanges[i]?.[compareField] === strip?.[compareField]) {
      hasBeenChanged = true;
      const stripChangeEl = cloneStripChanges[i];
      if (isReset) {
        indexRemove = i;
        break;
      }
      if (strip.type === type && !isEvent) {
        indexRemove = i;
        break;
      }
      if (stripChangeEl.oldType === type) {
        indexRemove = i;
        break;
      }
      if (stripChangeEl.type === type) {
        return cloneStripChanges;
      }
      stripChangeEl.type = type;
      stripChangeEl.oldType = stripChangeEl.oldType || stripChangeEl.type;
    }
  }
  if (!_.isNil(indexRemove)) {
    cloneStripChanges.splice(indexRemove, 1);
  }
  if (!hasBeenChanged && strip.type !== type) {
    cloneStripChanges.push({
      ...strip,
      ...{
        type,
        oldType: strip.type,
        isReviewed: strip.isReviewed,
        oldReviewed: strip.isReviewed,
      },
    });
  }
  return cloneStripChanges;
};

export const updateMultipleStripType = ({
  stripChanges = [] as any[],
  strips = [] as any[],
  type,
  isEvent = true,
}: {
  stripChanges?: any[];
  strips?: any[];
  type: any;
  isEvent?: boolean;
}) => {
  const cloneStripChanges = _.cloneDeep(stripChanges);
  const indexRemoves: any[] = [];
  const idHasBeenChangedStrips: any[] = [];
  let breakFirstLoop = false;
  let findCounter = 0;
  const isResettingChanges = !type;
  for (let i = 0, len = cloneStripChanges.length; i < len; i += 1) {
    for (let j = 0; j < strips.length; j += 1) {
      const compareField = isEvent ? 'idEvent' : 'id';
      if (cloneStripChanges[i]?.[compareField] === strips[j]?.[compareField]) {
        findCounter += 1;
        //* If done search for all strips then early break the loop
        if (findCounter === strips.length) {
          breakFirstLoop = true;
        }
        idHasBeenChangedStrips.push(strips[j].id);
        if (isResettingChanges || (strips[j].type === type && !isEvent)) {
          indexRemoves.push(i);
          break;
        }
        const stripChangeEl = cloneStripChanges[i];
        if (isResettingChanges || stripChangeEl.oldType === type) {
          indexRemoves.push(i);
          break;
        }
        if (stripChangeEl.type === type) {
          break;
        }
        stripChangeEl.type = type;
        stripChangeEl.oldType = stripChangeEl.oldType || stripChangeEl.type;
      }
    }
    if (breakFirstLoop) {
      break;
    }
  }

  //* Splice in reverse order to prevent mess up index
  for (let i = indexRemoves.length - 1; i >= 0; i -= 1) {
    cloneStripChanges.splice(indexRemoves[i], 1);
  }

  _.forEach(strips, (strip) => {
    if (!idHasBeenChangedStrips.includes(strip.id) && type && strip.type !== type) {
      cloneStripChanges.push({
        ...strip,
        ...{
          type,
          oldType: strip.type,
          isReviewed: strip.isReviewed,
          oldReviewed: strip.isReviewed,
        },
      });
    }
  });

  return cloneStripChanges;
};

/**
 * Records an edited strip capture against the event list, appending the event when it is not there yet. Locates the event by idEvent, walks its captures for one whose sample mark shares the incoming annotation's start and end time, overwrites that capture or appends a new one, then rolls every capture's annotations into the event's draw alongside the new strip images and technician comments.
 *
 * @param events - Current event list, deep-cloned before editing.
 * @param stripSelect - Strip being edited, used to locate the event and as the template when it is absent.
 * @param draw - Annotations for the capture, including the sample mark that identifies it.
 * @param allIndexStripSvg - Rendered strip images stored on the capture and the event.
 * @param technicianComments - Technician comments attached to the capture and the event.
 * @returns The cloned event list with the capture recorded.
 */
export const updateListImagesChangeV2 = (
  events: any[],
  stripSelect: any,
  draw: any,
  allIndexStripSvg: any,
  technicianComments: any,
) => {
  try {
    const data = _.cloneDeep(events);
    const ele = _.find(data, (x) => x.idEvent === stripSelect?.idEvent);
    const findDraw = _.find(draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
    if (ele) {
      const captures = _.cloneDeep(ele.captures || []);
      let isReplace = false;
      const drawData = [];
      _.forEach(captures, (capture) => {
        const sampleMark = _.find(capture.draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
        if (
          sampleMark?.startTime === findDraw?.startTime &&
          sampleMark?.endTime === findDraw?.endTime
        ) {
          _.assign(capture, {
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            action: 'update',
          });
          isReplace = true;
          drawData.push(...draw);
        } else {
          drawData.push(...capture.draw);
        }
      });
      if (!isReplace) {
        captures.push({
          draw,
          stripImgs: allIndexStripSvg,
          technicianComments,
          captureId: new Date().getTime(),
          isNewCapture: true,
          action: 'update',
        });
        drawData.push(...draw);
      }
      _.assign(ele, {
        draw: drawData,
        stripImgs: allIndexStripSvg,
        technicianComments,
        isUpdateImage: true,
        captures,
      });
    } else {
      const captures = _.cloneDeep(stripSelect.captures || []);
      const drawData = [];
      let isReplace = false;
      _.forEach(captures, (capture) => {
        const sampleMark = _.find(capture.draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
        if (
          sampleMark?.startTime === findDraw?.startTime &&
          sampleMark?.endTime === findDraw?.endTime
        ) {
          _.assign(capture, {
            draw,
            stripImgs: allIndexStripSvg,
            technicianComments,
            action: 'update',
          });
          isReplace = true;
          drawData.push(...draw);
        } else {
          drawData.push(...capture.draw);
        }
      });

      if (!isReplace) {
        captures.push({
          draw,
          stripImgs: allIndexStripSvg,
          technicianComments,
          captureId: new Date().getTime(),
          isNewCapture: true,
          action: 'update',
        });
        drawData.push(...draw);
      }

      data.push({
        ...stripSelect,
        originEvent: stripSelect,
        draw: drawData,
        stripImgs: allIndexStripSvg,
        technicianComments,
        isUpdateImage: true,
        captures,
      });
    }

    return data;
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * Folds a round of beat marker edits into the accumulated beat-change list. For each Add, Remove or Edit it looks up an existing change at the same time and either drops it when the edit restores the server's original type, reassigns type and oldType, or appends a new record with local insertions flagged isLocalBeat, then deletes every id collected for removal.
 *
 * @param updatedBeats - Marker deltas carrying actionType, time, type, oldType and cellId.
 * @param beatChanges - Accumulated change list, cloned rather than mutated.
 * @returns The updated change list, or beatChanges unchanged when there were no deltas.
 */
export const handleUpdateBeatChanges = (updatedBeats: any[], beatChanges: any[]) => {
  if (updatedBeats.length === 0) {
    return beatChanges;
  }
  const clonedBeatChanges: any[] = _.cloneDeep(beatChanges);
  const idRemoves: any[] = [];
  _.forEach(updatedBeats, (b: any) => {
    const { actionType, time, type, oldType, cellId } = b;
    /*
      Server beat change
      {
        id: 1648761703332
        oldType: "N"
        type: "Q"
      }
      Local beat change
      {
        id: 1648761703332
        type: "Q"
        isLocalBeat: true,
      }
    */
    if (actionType === 'Add') {
      // N/S/V
      const eleAdd = clonedBeatChanges.find((x) => x.id === time);
      if (eleAdd) {
        if (eleAdd.oldType === type) {
          // case: change type server beat (N,S,V,Q,Del) => beatChanges have beat => add beat same original type & same position (time) => remove
          idRemoves.push(eleAdd.id);
        } else {
          // case change type server || local beat => beatChanges have beat => add beat different original type & same position (time) => assign type
          _.assign(eleAdd, { type, oldType });
        }
      } else {
        // case first add new local beat
        clonedBeatChanges.push({
          id: time,
          type,
          isLocalBeat: true,
          cellId,
        });
      }
    }
    if (actionType === 'Remove') {
      const eleRemove = clonedBeatChanges.find((x) => x.id === time);
      if (eleRemove) {
        if (eleRemove.isLocalBeat) {
          // case delete local beat => remove
          idRemoves.push(eleRemove.id);
        } else {
          // case delete server beat => assign deleted type
          _.assign(eleRemove, { type, oldType });
        }
      } else {
        // case first deleted server beat
        clonedBeatChanges.push({
          id: time,
          type,
          oldType,
          cellId,
        });
      }
    }
    if (actionType === 'Edit') {
      const eleEdit = clonedBeatChanges.find((x) => x.id === time);
      if (eleEdit) {
        if (eleEdit.isLocalBeat) {
          // case edit local beat
          _.assign(eleEdit, { type, oldType });
        } else if (eleEdit.oldType === type) {
          // case edit server beat same original type
          idRemoves.push(eleEdit.id);
        } else {
          // case edit server beat different original type
          _.assign(eleEdit, { type, oldType });
        }
      } else {
        // case first edit server beat
        clonedBeatChanges.push({
          id: time,
          type,
          oldType,
          cellId,
        });
      }
    }
  });
  if (idRemoves.length) {
    _.remove(clonedBeatChanges, (x) => idRemoves.includes(x.id));
  }
  return clonedBeatChanges;
};

/**
 * Converts a beat's epoch into its sample index within a strip. Divides the millisecond difference by one thousand and rounds after scaling by the configured ECG sampling frequency.
 *
 * @param beatEpoch - Epoch of the beat in milliseconds.
 * @param startEpoch - Epoch of the strip's first sample.
 * @returns The beat's sample index relative to startEpoch.
 */
export const calculateBeatPosition = (beatEpoch: number, startEpoch: number) => {
  const beatDiffSeconds = (beatEpoch - startEpoch) / 1000;
  const beatPosition = Math.round(beatDiffSeconds * CONFIG_ECG.samplingFrequency);
  return beatPosition;
};

/**
 * Replays the pending beat edits onto a strip's beat and status arrays. Keeps only changes whose epoch falls inside the window, converts each to a sample position, and for N, S, V or Q either overwrites a beat lying within one sample or inserts it in order, while any other type deletes the matching beat and its status.
 *
 * @param beats - Sample positions of the beats currently drawn.
 * @param hesBeatStatus - Numeric statuses running parallel to beats.
 * @param beatChanges - Pending edits, each keyed by its epoch in id.
 * @param startEpoch - Window start epoch, also the origin for sample positions.
 * @param stopEpoch - Window stop epoch.
 * @returns An object carrying updatedBeats and updatedHesBeatStatus.
 */
export const generateUpdatedBeats = (
  beats: any[] = [],
  hesBeatStatus: any[] = [],
  beatChanges: any[] = [],
  startEpoch: any = '',
  stopEpoch: any = '',
) => {
  const updatedBeats = [...beats];
  const updatedHesBeatStatus = [...hesBeatStatus];
  /**
   * Narrows the pending beat edits to those falling inside the drawn strip. Keeps the changes whose id epoch sits between startEpoch and stopEpoch, and stays undefined when beatChanges itself is unset.
   *
   * @returns The in-window beat changes, or the unset beatChanges value.
   */
  const filterBeatChanges =
    beatChanges && beatChanges.filter((x) => x.id >= startEpoch && x.id <= stopEpoch);
  _.forEach(filterBeatChanges, (item) => {
    const beatPosition = calculateBeatPosition(item.id, startEpoch);
    const compareBeatPositions = [beatPosition - 1, beatPosition, beatPosition + 1];
    if (_.includes(['N', 'S', 'V', 'Q'], item.type)) {
      const beatTypeNumber = generateHesBeatStatus(item.type);
      const editIndex = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      if (editIndex > -1) {
        updatedBeats[editIndex] = beatPosition;
        updatedHesBeatStatus[editIndex] = beatTypeNumber;
      } else {
        const addIndex = updatedBeats.findIndex((x) => x > beatPosition);
        if (addIndex > -1) {
          updatedBeats.splice(addIndex, 0, beatPosition);
          updatedHesBeatStatus.splice(addIndex, 0, beatTypeNumber);
        } else {
          updatedBeats.push(beatPosition);
          updatedHesBeatStatus.push(beatTypeNumber);
        }
      }
    } else {
      const index = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      updatedBeats.splice(index, 1);
      updatedHesBeatStatus.splice(index, 1);
    }
  });
  return {
    updatedBeats,
    updatedHesBeatStatus,
  };
};

export const generateUpdatedBeatsV2 = ({
  beats = [] as any[],
  hesBeatStatus = [] as any[],
  beatChanges = [] as any[],
  startEpoch,
  stopEpoch,
  beatHeatMapIds,
  applyBulkBeatsChanges,
  resetAppliedBulkBeatsChanges,
  beatTemplateIds,
}: {
  beats?: any[];
  hesBeatStatus?: any[];
  beatChanges?: any[];
  startEpoch: any;
  stopEpoch: any;
  beatHeatMapIds: any;
  applyBulkBeatsChanges: any;
  resetAppliedBulkBeatsChanges: any;
  beatTemplateIds: any;
}) => {
  const updatedBeats: any[] = [...beats];
  const updatedHesBeatStatus: any[] = [...hesBeatStatus];
  if (!_.isEmpty(applyBulkBeatsChanges)) {
    const resetBeatIndex: any[] = [];
    if (!_.isEmpty(resetAppliedBulkBeatsChanges)) {
      const filterResetAppliedBulkBeatsChanges = resetAppliedBulkBeatsChanges.filter(
        (x: any) => x.id >= startEpoch && x.id <= stopEpoch,
      );
      _.forEach(filterResetAppliedBulkBeatsChanges, (x: any) => {
        const position = calculateBeatPosition(x.id, startEpoch);
        const compareBeatPositions = [position - 1, position, position + 1];
        const resetIndex = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
        resetBeatIndex.push(resetIndex);
      });
    }
    _.forEach(beatHeatMapIds, (cellId: any, index: number) => {
      const changeCell = findInBulkChanges(applyBulkBeatsChanges, {
        templateId: beatTemplateIds?.[index],
        cellId,
      });
      if (changeCell && !_.includes(resetBeatIndex, index)) {
        if (_.includes(['N', 'S', 'V', 'Q'], changeCell.type)) {
          updatedHesBeatStatus[index] = generateHesBeatStatus(changeCell.type);
        } else {
          const beat = beats[index];
          const beatIndex = updatedBeats.findIndex((x) => x === beat);
          updatedBeats.splice(beatIndex, 1);
          updatedHesBeatStatus.splice(beatIndex, 1);
        }
      }
    });
  }

  /**
   * Narrows the pending beat edits to those falling inside the drawn strip. Keeps the changes whose id epoch sits between startEpoch and stopEpoch, and stays undefined when beatChanges itself is unset.
   *
   * @returns The in-window beat changes, or the unset beatChanges value.
   */
  const filterBeatChanges =
    beatChanges && beatChanges.filter((x) => x.id >= startEpoch && x.id <= stopEpoch);
  _.forEach(filterBeatChanges, (item) => {
    const beatPosition = calculateBeatPosition(item.id, startEpoch);
    const compareBeatPositions = [beatPosition - 1, beatPosition, beatPosition + 1];
    if (_.includes(['N', 'S', 'V', 'Q'], item.type)) {
      const beatTypeNumber = generateHesBeatStatus(item.type);
      const editIndex = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      if (editIndex > -1) {
        updatedBeats[editIndex] = beatPosition;
        updatedHesBeatStatus[editIndex] = beatTypeNumber;
      } else {
        const addIndex = updatedBeats.findIndex((x) => x > beatPosition);
        if (addIndex > -1) {
          updatedBeats.splice(addIndex, 0, beatPosition);
          updatedHesBeatStatus.splice(addIndex, 0, beatTypeNumber);
        } else {
          updatedBeats.push(beatPosition);
          updatedHesBeatStatus.push(beatTypeNumber);
        }
      }
    } else {
      // *: Delete
      const index = updatedBeats.findIndex((x) => _.includes(compareBeatPositions, x));
      updatedBeats.splice(index, 1);
      updatedHesBeatStatus.splice(index, 1);
    }
  });
  return {
    updatedBeats,
    updatedHesBeatStatus,
  };
};

/**
 * Diffs two beat marker sets into the Add, Remove and Edit deltas the server expects. Converts every marker back to an epoch from startEpoch, floors added markers onto the sampling-frequency grid, tags removals with the deleted type plus the original letter, and emits an Edit only where the current and previous statuses differ.
 *
 * @param currentBeatMarkers - Beat sample positions as they stand now.
 * @param prevBeatMarkers - Beat sample positions before the edit.
 * @param currentHesBeatStatus - Statuses parallel to currentBeatMarkers.
 * @param prevHesBeatStatus - Statuses parallel to prevBeatMarkers.
 * @param originalHesBeatStatus - Statuses as the server first sent them, used for oldType.
 * @param startEpoch - Epoch of the strip's first sample.
 * @param samplingFrequency - Sampling rate used to floor added marker epochs onto the grid.
 * @returns The marker deltas, each carrying time, type, an optional oldType and an actionType.
 */
export const getUpdatedBeatMarker = (
  currentBeatMarkers: any[],
  prevBeatMarkers: any[],
  currentHesBeatStatus: any[],
  prevHesBeatStatus: any[],
  originalHesBeatStatus: any[],
  startEpoch: number,
  samplingFrequency: number,
) => {
  const updatedBeatMarkers: any[] = [];
  // *: Has at current but not previous
  const beatMarkersAdded = _.difference(currentBeatMarkers, prevBeatMarkers);
  const dividedNumber = samplingFrequency / 250;
  _.forEach(beatMarkersAdded, (item: any) => {
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    const epoch = startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000;
    updatedBeatMarkers.push({
      time: Math.floor(epoch / dividedNumber) * dividedNumber,
      type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
      actionType: 'Add',
    });
  });

  // *: Has at previous but not current
  const beatMarkersRemoved = _.difference(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersRemoved, (item: any) => {
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    updatedBeatMarkers.push({
      time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
      type: TYPE_BEAT_ENUM.DELETED,
      oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
      actionType: 'Remove',
    });
  });

  // *: Has both
  const beatMarkersIntersection = _.intersection(prevBeatMarkers, currentBeatMarkers);
  _.forEach(beatMarkersIntersection, (item: any) => {
    // *: Compare hesBeatStatus in currentHesBeatStatus (with currentIndex) to originalHesBeatStatus (with originalIndex)
    const currentBeatIndex = _.findIndex(currentBeatMarkers, (x) => x === item);
    const prevBeatIndex = _.findIndex(prevBeatMarkers, (x) => x === item);
    if (!isSameBeats(currentHesBeatStatus[currentBeatIndex], prevHesBeatStatus[prevBeatIndex])) {
      updatedBeatMarkers.push({
        time: startEpoch + (item / CONFIG_ECG.samplingFrequency) * 1000,
        type: generateBeatTypeString(currentHesBeatStatus[currentBeatIndex]),
        oldType: generateBeatTypeString(originalHesBeatStatus[prevBeatIndex]),
        actionType: 'Edit',
      });
    }
  });
  return updatedBeatMarkers;
};

/**
 * Picks the channel that carried most of the beats in a selection. Tallies the occurrences into a three-slot histogram indexed by channel minus one and returns the one-based index of the largest bucket.
 *
 * @param beatChannels - Per-beat channel numbers, one-based.
 * @returns The one-based channel number seen most often.
 */
export const countBeatChannels = (beatChannels: any[] = []) => {
  const channelFreq = beatChannels.reduce(
    (acc, curr) => {
      acc[curr - 1] += 1;
      return acc;
    },
    [0, 0, 0],
  );
  return _.indexOf(channelFreq, _.max(channelFreq)) + 1;
};

/**
 * Marks a batch of beat changes as in flight before they are sent to the server. Returns the input untouched when it is empty, otherwise deep-clones it and sets isSaving on every entry.
 *
 * @param arr - Beat change records about to be saved.
 * @returns The cloned records with isSaving set, or the original array when it was empty.
 */
export const addIsSavingToChangedData = (arr: any[]) => {
  if (_.isEmpty(arr)) {
    return arr;
  }
  const cloneArr = _.cloneDeep(arr);
  _.forEach(cloneArr, (x: any) => {
    x.isSaving = true;
  });
  return cloneArr;
};

/**
 * Finds the pending bulk edit that already covers a given beat. Matches on cellId and originalTemplateId together when the change carries both, otherwise on whichever one is set, and skips changes carrying neither.
 *
 * @param bulkChanges - Pending bulk edits to search.
 * @param beat - Beat whose cellId and templateId are matched against the edits.
 * @returns The matching bulk change, or undefined when none applies.
 */
export const findInBulkChanges = (bulkChanges: any[], beat: any): any =>
  _.find(bulkChanges, (x: any) => {
    if (x.originalTemplateId && x.cellId) {
      return x.cellId === beat?.cellId && x.originalTemplateId === beat?.templateId;
    }
    if (x.cellId) {
      return x.cellId === beat?.cellId;
    }
    if (x.originalTemplateId) {
      return x.originalTemplateId === beat?.templateId;
    }
    return false;
  });

/**
 * Drops the entries flagged as in flight once their save has completed. Counts the isSaving entries while filtering and hands back the original array untouched when none were found.
 *
 * @param data - Records to sweep, some possibly carrying isSaving.
 * @returns The array without its isSaving entries, or the original array when none were flagged.
 */
export const removeSavingData = (data: any[]) => {
  let count = 0;
  const filterResult = data.filter((x: any) => {
    if (x.isSaving) {
      count += 1;
      return false;
    }
    return true;
  });
  if (count === 0) {
    return data;
  }
  return filterResult;
};
